import type { LocalStatusCheck } from "./DiagnosticsSettingsPanel.logic";
import type {
  CodexReadinessBlockCode,
  CodexRuntimeReadiness,
  CodexRuntimeReadinessFailureDetails,
} from "./codexRuntimeReadiness";

type ProviderStatus = Pick<LocalStatusCheck, "state" | "tone" | "evidence">;

const BLOCKED_PROVIDER_STATUS: Readonly<Record<CodexReadinessBlockCode, ProviderStatus>> = {
  CODEX_RECONNECT_REQUIRED: {
    state: "Reconnect",
    tone: "error",
    evidence: "Reconnect ChatGPT in Settings → Providers, then recheck this Environment.",
  },
  CODEX_ACCOUNT_IDENTITY_MISMATCH: {
    state: "Account changed",
    tone: "error",
    evidence: "This runtime received a different ChatGPT account. Reconnect the project.",
  },
  RUNTIME_AUTHORITY_UPGRADE_BLOCKED: {
    state: "Upgrade blocked",
    tone: "error",
    evidence:
      "Runtime authority upgrade is blocked by another lifecycle operation. Review Runtime events, then recheck.",
  },
  RUNTIME_EXACT_AUTHORITY_REQUIRED: {
    state: "Reconnect project",
    tone: "error",
    evidence: "Reconnect this project to refresh exact-machine authority, then recheck.",
  },
  RUNTIME_AUTH_REQUIRED: {
    state: "Reconnect project",
    tone: "error",
    evidence: "Reconnect this project to refresh exact-machine authority, then recheck.",
  },
  RUNTIME_AUTH_FORBIDDEN: {
    state: "Reconnect project",
    tone: "error",
    evidence: "Reconnect this project to refresh exact-machine authority, then recheck.",
  },
  CMMD_AUTH_REQUIRED: {
    state: "Sign in",
    tone: "error",
    evidence: "Sign in to CMMD again, then recheck this Environment.",
  },
  CMMD_PERMISSION_DENIED: {
    state: "Access denied",
    tone: "error",
    evidence: "Ask a CMMD organization administrator for access to this Environment, then recheck.",
  },
  CMMD_RUNTIME_CONNECT_TIMEOUT: {
    state: "Connection timed out",
    tone: "error",
    evidence: "CMMD did not finish the Environment connection check. Review Runtime events.",
  },
  CMMD_RUNTIME_AUTHORITY_UPGRADE_TIMEOUT: {
    state: "Upgrade pending",
    tone: "error",
    evidence: "The runtime authority upgrade did not finish before the readiness deadline.",
  },
  CMMD_RUNTIME_AUTHORITY_UPGRADE_STATUS_TIMEOUT: {
    state: "Upgrade status timed out",
    tone: "error",
    evidence: "CMMD did not return the durable authority-upgrade status. Review Runtime events.",
  },
  CMMD_RUNTIME_RECONNECT_TIMEOUT: {
    state: "Reconnect timed out",
    tone: "error",
    evidence: "CMMD did not finish reconnecting the upgraded runtime. Review Runtime events.",
  },
  CMMD_RUNTIME_CREDENTIAL_TIMEOUT: {
    state: "Credential check timed out",
    tone: "error",
    evidence: "CMMD did not finish verifying the current Codex credential.",
  },
  CMMD_RUNTIME_UNAVAILABLE: {
    state: "CMMD unavailable",
    tone: "error",
    evidence: "CMMD could not verify this Environment. Recheck or review Runtime events.",
  },
  CMMD_UNAVAILABLE: {
    state: "CMMD unavailable",
    tone: "error",
    evidence: "CMMD could not verify this Environment. Recheck or review Runtime events.",
  },
  INVALID_RUNTIME_REQUEST: {
    state: "Unavailable",
    tone: "error",
    evidence: "This project runtime is missing its CMMD organization identity.",
  },
  INVALID_MUTATION_ORIGIN: {
    state: "Request blocked",
    tone: "error",
    evidence: "Forge rejected the readiness request origin. Reload Forge and recheck.",
  },
  RUNTIME_DELIVERY_RECEIPT_INVALID: {
    state: "Invalid proof",
    tone: "error",
    evidence: "The runtime returned invalid Codex delivery proof. Do not retry this chat.",
  },
  RUNTIME_READINESS_UNAVAILABLE: {
    state: "Runtime unavailable",
    tone: "error",
    evidence: "The runtime could not verify Codex delivery. Recheck or review Runtime events.",
  },
  READINESS_RESPONSE_INVALID: {
    state: "Unavailable",
    tone: "error",
    evidence: "Exact Codex delivery could not be verified. Recheck or review Runtime events.",
  },
  READINESS_UNAVAILABLE: {
    state: "Unavailable",
    tone: "error",
    evidence: "Exact Codex delivery could not be verified. Recheck or review Runtime events.",
  },
};

function withReadinessFailureBoundary(
  status: ProviderStatus,
  details: CodexRuntimeReadinessFailureDetails | undefined,
): ProviderStatus {
  if (!details) {
    return status;
  }
  const upstream =
    details.upstreamStatus === null
      ? "before CMMD replied"
      : `at CMMD HTTP ${details.upstreamStatus}`;
  return {
    ...status,
    evidence: `${status.evidence} Last check stopped during ${details.phase.replaceAll("_", " ")} ${upstream}.`,
  };
}

export function cloudProviderStatus(input: {
  readonly codexReadiness: CodexRuntimeReadiness;
  readonly sessionAuthState: "authenticated" | "requires-auth" | "unknown";
}): ProviderStatus {
  if (input.sessionAuthState === "requires-auth") {
    return {
      state: "Attention",
      tone: "error",
      evidence: "Forge runtime session requires authentication",
    };
  }
  if (input.codexReadiness.status === "checking") {
    return {
      state: "Checking",
      tone: "info",
      evidence: "Verifying exact Codex delivery to this Environment…",
    };
  }
  if (input.codexReadiness.status === "ready") {
    const account = input.codexReadiness.accountEmail ?? "Connected ChatGPT account";
    const plan = input.codexReadiness.planType
      ? ` · ${input.codexReadiness.planType[0]?.toUpperCase()}${input.codexReadiness.planType.slice(1)}`
      : "";
    return {
      state: "Ready",
      tone: "ready",
      evidence: `Codex · ${account}${plan} · GPT-5.6 Sol · exact machine epoch ${input.codexReadiness.credentialEpoch}`,
    };
  }
  if (input.codexReadiness.status === "blocked") {
    return withReadinessFailureBoundary(
      BLOCKED_PROVIDER_STATUS[input.codexReadiness.code],
      input.codexReadiness.details,
    );
  }
  if (input.codexReadiness.status === "not_checked") {
    return {
      state: "Not checked",
      tone: "muted",
      evidence: "Select Recheck to verify exact Codex delivery.",
    };
  }
  return {
    state: "Unavailable",
    tone: "error",
    evidence: "Exact Codex delivery could not be verified. Recheck or review Runtime events.",
  };
}
